'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const gtmFormSchema = z.object({
  containerId: z.string()
    .min(1, 'Container ID is required')
    .regex(/^(GTM|G|AW)-[A-Z0-9]+$/, 'Invalid GTM Container ID format. It should be in the format GTM-XXXXXX, G-XXXXXX or AW-XXXXXX'),
  googleAdsConversionId: z.string().optional(),
  googleAdsConversionLabel: z.string().optional(),
  events: z.object({
    PageView: z.boolean(),
    InitiateCheckout: z.boolean(),
    Purchase: z.boolean(),
    AddToCart: z.boolean(),
    ViewContent: z.boolean(),
  }),
})

type FormValues = z.infer<typeof gtmFormSchema>

interface GTMFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function GTMForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: GTMFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(gtmFormSchema),
    defaultValues: {
      containerId: defaultValues.settings?.containerId ?? '',
      googleAdsConversionId: defaultValues.settings?.googleAdsConversionId ?? '',
      googleAdsConversionLabel: defaultValues.settings?.googleAdsConversionLabel ?? '',
      events: {
        PageView: defaultValues.settings?.events?.PageView ?? true,
        InitiateCheckout: defaultValues.settings?.events?.InitiateCheckout ?? true,
        Purchase: defaultValues.settings?.events?.Purchase ?? true,
        AddToCart: defaultValues.settings?.events?.AddToCart ?? true,
        ViewContent: defaultValues.settings?.events?.ViewContent ?? true,
      },
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { events, ...settings } = data
    await onSubmit({
      settings: {
        ...settings,
        events,
      },
    })
  }

  const EVENTS = {
    PageView: 'forms.common.events.pageView',
    InitiateCheckout: 'forms.common.events.initiateCheckout',
    Purchase: 'forms.common.events.purchase',
    AddToCart: 'forms.common.events.addToCart',
    ViewContent: 'forms.common.events.viewContent',
  } as const

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Info Alert */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{t('gtm.aboutTitle')}</AlertTitle>
        <AlertDescription>
          {t('gtm.aboutDescription')}
        </AlertDescription>
      </Alert>

      {/* Container Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gtm.containerConfiguration')}</CardTitle>
          <CardDescription>{t('gtm.containerConfigDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="containerId">
          {t('gtm.containerId')}
              <span className="ml-1 text-destructive">*</span>
        </Label>
        <Input
          id="containerId"
              placeholder="GTM-XXXXXX"
          disabled={isLoading}
          {...register('containerId')}
        />
        <p className="text-xs text-muted-foreground">
              {t('gtm.containerIdHint')}
        </p>
        {errors.containerId && (
          <p className="text-xs text-destructive">{errors.containerId.message}</p>
        )}
      </div>
        </CardContent>
      </Card>

      {/* Event Tracking */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gtm.eventTracking')}</CardTitle>
          <CardDescription>{t('gtm.eventTrackingDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(EVENTS).map(([key, label]) => (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={`event-${key}`}
                checked={watch(`events.${key as keyof typeof EVENTS}`)}
                onCheckedChange={(checked) => setValue(`events.${key as keyof typeof EVENTS}`, checked as boolean)}
                disabled={isLoading}
              />
                <Label htmlFor={`event-${key}`} className="cursor-pointer font-normal">
                {t(label)}
              </Label>
            </div>
          ))}
        </div>
        </CardContent>
      </Card>

      {/* Google Ads Integration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gtm.googleAdsIntegration')}</CardTitle>
          <CardDescription>
            {t('gtm.googleAdsDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
      <div className="space-y-2">
            <Label htmlFor="gtmIntegrationGoogleConversionId">
              {t('gtm.conversionId')}
            </Label>
          <Input
            id="gtmIntegrationGoogleConversionId"
              placeholder="AW-XXXXXXXXX"
            disabled={isLoading}
            {...register('googleAdsConversionId')}
          />
        </div>

        <div className="space-y-2">
            <Label htmlFor="gtmIntegrationGoogleConversionLabel">
              {t('gtm.conversionLabel')}
            </Label>
          <Input
            id="gtmIntegrationGoogleConversionLabel"
              placeholder={t('gtm.conversionLabelPlaceholder')}
            disabled={isLoading}
            {...register('googleAdsConversionLabel')}
          />
        </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gtm.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('gtm.setupStep1')}{' '}
                <a
                  href="https://tagmanager.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('gtm.setupStep1Link')}
                </a>
              </li>
              <li>{t('gtm.setupStep2')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('gtm.setupStep3') }} />
              <li>{t('gtm.setupStep4')}</li>
            </ol>
      </div>
        </CardContent>
      </Card>
    </form>
  )
}
